import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Size an element to the rest of the viewport below wherever it happens to sit.
 *
 * A workbench page wants to own the screen, but `calc(100vh - 96px)` guesses at
 * what is above it, and the guess is wrong the moment anything else is: the
 * email-verification banner alone pushes the inbox a hundred pixels past the
 * fold. This measures the element's own top edge instead, so the page fits
 * whether or not a banner is showing.
 *
 * Re-measures on resize and whenever the content above it changes size.
 */
export function useFillViewport(bottomGap = 24) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState<number | undefined>(undefined);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    setHeight(Math.max(320, window.innerHeight - top - bottomGap));
  }, [bottomGap]);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);

    // The banner above can appear, be dismissed or reflow at any time, and it
    // moves this element's top edge without a window resize.
    const parent = ref.current?.parentElement;
    const observer = parent ? new ResizeObserver(measure) : null;
    if (parent && observer) observer.observe(parent);

    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [measure]);

  return { ref, height };
}
